import * as Yup from 'yup'
import { getIn, setIn } from 'formik'
import { logger } from '../actions'
const emailRegexp = getIn(Yup.string().email(), 'tests[0].OPTIONS.params.regex')
// export const emailDomainRegex = /^[^@]+@([a-zA-Z0-9.-]+\.(com|ltd|nyc|cl|at|cz|pl|eu|org|gt|net|edu|gov|mil|info|biz|online|store|app|dev|aero|ai|bg|co|me|io|xyz|tech|club|live|site|vip|pro|in|ae|se|uk|us|ca|de|id|fr|jp|cn|au|br|ru|tv|sg|za|travel|dk|hr|mx|es|it|ee|qa|sa|kw|gr|gb|cy|ch|limo|be|bh|mc|au|ma|lu|mt|nz|om|pr|pe|eg|gl|tr|ie|tt|jm|jo|lv|si|hamburg|na|mobi|vn|finance|global|services|hk|events|nl|pt|lt|no|sgh\.events|com\.ar|com\.do|com\.mx|com\.py|co\.il))$/i
export const emailDomainRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const defaultErrors = {
  required: 'Required',
  email: 'Invalid email',
  emailsList: 'One or more email addresses are invalid',
  checkExisting: 'Such item has already exists!',
  minLength: 'Too short field value!',
  requiredLocation: 'Please enter a location',
  requiredDate: 'Please enter a date',
  requiredTime: 'Please enter a time',
}

export const stringValidator = ({ defaultValue = '' } = {}) =>
  Yup.string()
    .nullable()
    .default(defaultValue)

export const requiredStringValidator = ({
  message = defaultErrors.required,
  defaultValue = '',
} = {}) => stringValidator({ defaultValue }).required(message)

export const requiredNumberValidator = ({
  message = defaultErrors.required,
  defaultValue = 0,
} = {}) =>
  Yup.number()
    .default(defaultValue)
    .required(message)
export const minDigitsValidator = ({
  limit = 7,
  message = `Minimum ${limit} digits required`,
  defaultValue = '',
} = {}) =>
  stringValidator({ defaultValue })
    .required(message)
    .matches(new RegExp(`^\\d{${limit},}$`), message)

export const requiredStringValidatorNum = ({
  message = defaultErrors.required,
  defaultValue = '',
} = {}) =>
  stringValidator({ defaultValue })
    .required(message)
    .matches(/^[a-zA-Z\s]*$/, 'Only alphabetic characters are allowed')

export const booleanValidator = ({ defaultValue = false } = {}) =>
  Yup.boolean().default(defaultValue)

export const emailValidator = ({
  message = defaultErrors.email,
  defaultValue = '',
} = {}) =>
  Yup.string()
    .email(message)
    .matches(emailDomainRegex, 'Email domain must have a valid TLD')
    .default(defaultValue)
// Yup.string()
//   .email(message)
//   .default(defaultValue)

// const testEmailsList = value => {
//   let invalidList = []
//   let emails = (value ? value.split(',') : []).map(email => email.trim())

//   emails.forEach(email => {
//     if (!emailRegexp.test(email)) {
//       invalidList.push(email)
//     }
//   })

//   return !invalidList.length
// }

const testEmailsList = value => {
  let invalidList = []
  const emails = (value ? value.split(',') : []).map(email => email.trim())

  emails.forEach(email => {
    const isValidEmail = Yup.string()
      .email()
      .matches(emailDomainRegex)
      .isValidSync(email) // run Yup validation synchronously

    if (!isValidEmail) {
      invalidList.push(email)
    }
  })

  return !invalidList.length
}

Yup.addMethod(Yup.string, 'emailsList', function(message) {
  return this.test({
    name: 'emailsList',
    message: message || defaultErrors.emailsList,
    test: testEmailsList,
  })
})

export const multipleEmailValidator = ({ message } = {}) =>
  Yup.string().emailsList(message)

// Passenger email ("Ask for passenger's email" company setting). emailValidator() on
// its own rejects an empty value - its `matches` has no excludeEmptyString - so the
// format rule here skips an empty one: that is what lets the email be optional, and
// when it is mandatory an empty field reports "Enter email address" alone. Chained
// after requiredStringValidator() instead, emailValidator() would add its own "Email
// domain must have a valid TLD" for the same empty value, and which of the two Formik
// shows depends on how deeply the field is nested.
// Spaces around the address are ignored (it is saved trimmed), so every rule here looks
// at the trimmed value: a pasted " john@mail.com " is fine, and "   " counts as empty.
// Yup's own trim() cannot be used instead - inside an array of lazy() schemas, as the
// VIP passengers are, it only reports "must be a trimmed string".
export const passengerEmailValidator = ({ mandatory = false } = {}) => {
  const emailFormat = stringValidator().test(
    'optional-email',
    defaultErrors.email,
    value =>
      !value || !value.trim()
        ? true
        : emailValidator().isValidSync(value.trim())
  )
  return mandatory
    ? emailFormat.test(
        'required-email',
        'Enter email address',
        value => !!value && !!value.trim()
      )
    : emailFormat
}

export const fileRequired = ({
  message = defaultErrors.required,
  defaultValue = null,
} = {}) =>
  Yup.mixed()
    .required(message)
    .default(defaultValue)

export const requiredObject = ({
  message = defaultErrors.required,
  defaultValue = null,
} = {}) =>
  Yup.object()
    .required(message)
    .default(defaultValue)
    .nullable()

const checkExisting = async (func, params) => {
  try {
    const result = await func(params)
    return !!result
  } catch (error) {
    logger({ fileName: 'formValidation-index', error: error })
    console.log(error)
  }
}

Yup.addMethod(Yup.string, 'checkExisting', function(func, params, message) {
  return this.test({
    name: 'checkExisting',
    message: message || defaultErrors.checkExisting,
    test: () => {
      checkExisting(func, params)
    },
  })
})

export const checkExistingValidator = ({ func, params, message } = {}) =>
  Yup.checkExisting(func, params, message)

export const minLengthStringValidator = ({
  limit,
  message,
  defaultValue = '',
} = {}) =>
  stringValidator({ defaultValue }).min(
    limit,
    (message = message || defaultErrors.minLength)
  )

export const requiredDependOnSelectValue = (name, dependOnValue) =>
  function(value) {
    const dependField = getIn(this.parent, name)

    // if (name === 'ppr') {
    //   return true
    // }

    return (
      !dependField ||
      dependField.value !== dependOnValue ||
      (dependField.value === dependOnValue && !!value)
    )
  }

export const handleSubmitValidationErrors = (
  { errors = {} } = {},
  form = {},
  parseName
) => {
  const { setErrors = () => {}, setSubmitting = () => {} } = form

  if (errors && errors.length) {
    let formErrors = {}

    errors.forEach(({ name, message }) => {
      try {
        let _name = parseName ? parseName(name) : name
        formErrors = setIn(formErrors, _name, message)
      } catch (error) {
        logger({ fileName: 'formValidation-index', error: error })
        console.log(error)
      }
    })

    setErrors(formErrors)
  }

  setSubmitting(false)
}
